import { useCallback, useMemo, useRef, useState } from "react";
import { ReformReturn } from "../../../types";
import {
  ErrorFormatConfig,
  ErrorTransformer,
  FormattedErrorMessage,
  FormErrorFormatter,
} from "./types";
import { FieldPath } from "../../form/form-groups";
import get from "lodash/get";
import { useMemoizedCallback } from "../../../common/useMemoizedCallback";

/**
 * Default error format configuration
 */
const defaultFormatConfig: ErrorFormatConfig = {
  format: "plain",
  includeLabels: true,
  includeRuleNames: false,
};

/**
 * Hook for formatting form error messages
 *
 * @param reform - Reform hook return value
 * @param initialConfig - Initial error format configuration
 * @returns Form error formatter methods
 */
export const useErrorFormatter = <T extends Record<string, any>>(
  reform: ReformReturn<T>,
  initialConfig?: Partial<ErrorFormatConfig>
): FormErrorFormatter<T> => {
  // Memoize the initial config to prevent unnecessary re-renders
  const memoizedInitialConfig = useMemo(() => initialConfig, [
    // Stringify the config to compare by value instead of reference
    JSON.stringify(initialConfig)
  ]);

  // Merge default config with initial config
  const [formatConfig, setFormatConfig] = useState<ErrorFormatConfig>(() => ({
    ...defaultFormatConfig,
    ...memoizedInitialConfig,
  }));

  // Store custom transformers
  const transformersRef = useRef<Record<string, ErrorTransformer<T>>>({});

  /**
   * Get the appropriate transformer for a field
   */
  const getTransformer = useCallback(
    (fieldPath: FieldPath<T>): ErrorTransformer<T> | undefined => {
      // Check for field-specific transformer
      if (transformersRef.current[fieldPath]) {
        return transformersRef.current[fieldPath];
      }

      // Check for global transformer
      if (transformersRef.current["*"]) {
        return transformersRef.current["*"];
      }

      return undefined;
    },
    [] // No dependencies as we're using ref
  );

  /**
   * Default transformer for error messages
   */
  const defaultTransformer = useMemoizedCallback(
    (
      message: string,
      fieldPath: FieldPath<T>,
      groupIndex: number
    ): FormattedErrorMessage => {
      const groups = reform.getGroups();
      const group = groups[groupIndex] || { id: "", data: {} as T };
      const labels = reform.config.labels || {};

      // Handle nested labels with lodash get
      const fieldLabel =
        typeof fieldPath === "string"
          ? get(labels, fieldPath) || get(labels, fieldPath as string)
          : undefined;

      let content = message;

      // Include field label if configured
      if (formatConfig.includeLabels && fieldLabel) {
        content = `${fieldLabel}: ${content}`;
      }

      return {
        content,
        format: formatConfig.format,
        fieldPath: fieldPath as string,
        fieldLabel,
        meta: {
          groupId: group.id,
          groupIndex,
        },
      };
    },
    [formatConfig, reform.getGroups, reform.config.labels]
  );

  /**
   * Format a field error message
   */
  const formatFieldError = useMemoizedCallback(
    (
      fieldPath: FieldPath<T>,
      message: string,
      groupIndex: number
    ): FormattedErrorMessage => {
      const transformer = getTransformer(fieldPath);
      const groups = reform.getGroups();
      const group = groups[groupIndex] || { id: "", data: {} as T };

      if (transformer) {
        return transformer.transform(message, fieldPath, {
          group,
          groupIndex,
          labels: reform.config.labels || {}, // Pass labels as is
          formatConfig,
        });
      }

      return defaultTransformer(message, fieldPath, groupIndex);
    },
    [defaultTransformer, formatConfig, getTransformer, reform.getGroups, reform.config.labels]
  );

  /**
   * Format a group-level error message
   */
  const formatGroupError = useMemoizedCallback(
    (message: string, groupIndex: number): FormattedErrorMessage => {
      const groups = reform.getGroups();
      const group = groups[groupIndex] || { id: "", data: {} as T };

      return {
        content: message,
        format: formatConfig.format,
        meta: {
          groupId: group.id,
          groupIndex,
          isGroupError: true,
        },
      };
    },
    [formatConfig.format, reform.getGroups]
  );

  /**
   * Format a form-level error message
   */
  const formatFormError = useCallback(
    (message: string): FormattedErrorMessage => {
      return {
        content: message,
        format: formatConfig.format,
        meta: {
          isFormError: true,
        },
      };
    },
    [formatConfig.format]
  );

  /**
   * Register a custom error transformer
   */
  const registerTransformer = useCallback(
    (fieldPath: FieldPath<T> | "*", transformer: ErrorTransformer<T>): void => {
      transformersRef.current[fieldPath as string] = transformer;
    },
    [] // No dependencies as we're using ref
  );

  /**
   * Update the error format configuration
   */
  const updateFormatConfig = useCallback(
    (config: Partial<ErrorFormatConfig>): void => {
      setFormatConfig((prev) => ({
        ...prev,
        ...config,
      }));
    },
    [] // No dependencies as we're using setState with function
  );

  // Return memoized object to prevent unnecessary re-renders
  return useMemo(
    () => ({
      formatFieldError,
      formatGroupError,
      formatFormError,
      registerTransformer,
      updateFormatConfig,
    }),
    [
      formatFieldError,
      formatGroupError,
      formatFormError,
      registerTransformer,
      updateFormatConfig,
    ]
  );
};
